import { sampleValues } from './fields.js';

/**
 * The tickets a design has to survive (D-89, PART 15).
 *
 * A preview against one tidy example proves nothing: the design that looks
 * right with "Adult" and "Sec 101 Row A Seat 12" is the one that loses the
 * event title when a school books under its full legal name, or prints the
 * entry time over the QR code for the one product that has an entry time.
 * These are the cases a designer is shown before publishing.
 */

export interface PreviewScenario {
  key: string;
  name: string;
  description: string;
  values: Record<string, string>;
}

const scenario = (key: string, name: string, description: string, overrides: Record<string, string>): PreviewScenario => ({
  key,
  name,
  description,
  // No table unless the scenario is about one: most seats are not at a table.
  values: { ...sampleValues(), 'ticket.table': '', ...overrides },
});

const NONE = '';

export const PREVIEW_SCENARIOS: PreviewScenario[] = [
  scenario('reserved', 'A reserved seat', 'The ordinary case: a seat, a price, a name.', {
    'ticket.type': 'Adult',
    'ticket.seat_label': 'Sec 101  Row A  Seat 12',
    'ticket.area': NONE,
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('table', 'A table seat', 'A seat at a numbered table, where the table is the thing people look for.', {
    'ticket.type': 'Adult',
    'ticket.seat_label': 'Table 12  Seat 3',
    'ticket.area': NONE,
    'ticket.section': NONE,
    'ticket.table': '12',
    'ticket.row': NONE,
    'ticket.seat': '3',
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('general_admission', 'General admission', 'No seat at all: the design must not leave a hole where the seat was.', {
    'ticket.type': 'General Admission',
    'ticket.seat_label': 'General admission',
    'ticket.area': 'Lawn',
    'ticket.section': NONE,
    'ticket.row': NONE,
    'ticket.seat': NONE,
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('timed_entry', 'A timed entry', 'The entry time is the product (D-86): it has to be unmissable.', {
    'event.title': 'Haunted Auditorium 2026',
    'ticket.type': 'Fast Pass',
    'ticket.seat_label': 'General admission',
    'ticket.area': 'Attraction Entry',
    'ticket.section': NONE,
    'ticket.row': NONE,
    'ticket.seat': NONE,
    'ticket.slot_time': '8:15 PM',
    'ticket.slot_window': '8:15 PM – 8:30 PM',
    'ticket.slot_date': 'Fri, Oct 23',
    'ticket.arrival_note': 'Arrive up to 5 minutes early; after 8:45 PM we will fit you in when we can',
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('comp', 'A comp', 'No price to print, and a reason that should show.', {
    'ticket.type': 'Comp',
    'price.face': 'Complimentary',
    'price.label': 'COMPLIMENTARY',
    'price.paid': NONE,
    'price.fees': NONE,
    'admission.comp_reason': 'Press',
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('long', 'Everything long', 'The names nobody plans for. If a design breaks, it breaks here.', {
    'event.title': 'The Burlington Municipal Band and Chorus Annual Holiday Spectacular',
    'event.subtitle': 'With the Southeast Iowa Youth Orchestra and special guests',
    'venue.name': 'Memorial Auditorium and Community Convention Center',
    'ticket.holder_name': 'Christopher Bartholomew Wainwright-Fitzgerald III',
    'ticket.type': 'Student / Senior / Military discount',
    'ticket.seat_label': 'Balcony Center  Sec 205  Row AA  Seat 118',
    'order.purchaser': 'Southeast Iowa Community School District Activities Office',
    'admission.terms':
      'One scan admits one. No re-entry once you leave the building. Photography without flash is permitted; recording is not. The newest issued code is the only valid one — earlier prints do not admit.',
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
  }),
  scenario('reissue', 'A reissued ticket', 'Somebody lost theirs; the old code must be visibly dead.', {
    'ticket.type': 'Adult',
    'admission.reissue_note': 'Reissued — earlier codes no longer admit',
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
  }),
  scenario('sparse', 'Almost nothing filled in', 'An event with no subtitle, no doors, no holder, no sponsor.', {
    'event.subtitle': NONE,
    'event.doors': NONE,
    'event.age_restriction': NONE,
    'event.performance_name': NONE,
    'ticket.holder_name': NONE,
    'ticket.notes': NONE,
    'order.purchaser': NONE,
    'sponsor.line': NONE,
    'sponsor.name': NONE,
    'price.paid': NONE,
    'price.fees': NONE,
    'ticket.slot_time': NONE,
    'ticket.slot_window': NONE,
    'ticket.slot_date': NONE,
    'ticket.arrival_note': NONE,
    'admission.comp_reason': NONE,
    'admission.reissue_note': NONE,
    'venue.gate': NONE,
  }),
];

export const scenarioByKey = (key: string): PreviewScenario | null => PREVIEW_SCENARIOS.find((s) => s.key === key) ?? null;
